import { useState, useEffect, useCallback } from 'react';
import { getOrderTracking } from '@/domains/api/tracking.api';
import { getEcho, reverbEnabled } from '@/utils/echo';
import { getSession } from '@/domains/auth/services/auth.service';
import type { ApiOrderTracking } from '@/domains/api/types';

function toCoord(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

export function useOrderTracking(orderId: number | null, pollIntervalMs = 15000) {
  const [tracking, setTracking] = useState<ApiOrderTracking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(reverbEnabled());

  const load = useCallback(async () => {
    if (!orderId) return;
    try {
      const data = await getOrderTracking(orderId);
      setTracking(data);
      setError(null);
    } catch {
      setError('Impossible de charger le suivi.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      setTracking(null);
      return;
    }
    setLoading(true);
    load();

    const token = getSession()?.token ?? null;
    const echo = getEcho(token);

    if (echo) {
      const channel = echo.private(`order.${orderId}`);
      channel.listen('.location.updated', (payload: {
        location: { latitude: number | string; longitude: number | string };
        eta_minutes?: number | null;
        distance_km?: number | null;
      }) => {
        setTracking((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            driver_location: {
              ...payload.location,
              latitude: payload.location.latitude,
              longitude: payload.location.longitude,
            },
            eta_minutes: payload.eta_minutes ?? prev.eta_minutes,
            distance_km: payload.distance_km ?? prev.distance_km,
            routing_source: 'osrm',
          } as ApiOrderTracking;
        });
      });
      setLive(true);

      return () => {
        channel.stopListening('.location.updated');
        echo.leave(`order.${orderId}`);
      };
    }

    const interval = setInterval(load, pollIntervalMs);
    setLive(false);
    return () => clearInterval(interval);
  }, [orderId, load, pollIntervalMs]);

  const pickup =
    tracking?.order.departure_latitude != null && tracking?.order.departure_longitude != null
      ? {
          lat: toCoord(tracking.order.departure_latitude)!,
          lng: toCoord(tracking.order.departure_longitude)!,
          label: tracking.order.departure_zone?.name ?? 'Collecte',
        }
      : null;

  const dropoff =
    tracking?.order.arrival_latitude != null && tracking?.order.arrival_longitude != null
      ? {
          lat: toCoord(tracking.order.arrival_latitude)!,
          lng: toCoord(tracking.order.arrival_longitude)!,
          label: tracking.order.arrival_zone?.name ?? 'Livraison',
        }
      : null;

  const driver =
    tracking?.driver_location
      ? {
          lat: toCoord(tracking.driver_location.latitude)!,
          lng: toCoord(tracking.driver_location.longitude)!,
          label: 'Livreur',
        }
      : null;

  return {
    tracking,
    loading,
    error,
    live,
    pickup,
    dropoff,
    driver,
    reload: load,
  };
}
